import { useEffect, useState } from 'react';
import { getPlaceholder } from 'utils/getPlaceHolder';
// material-ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Typography,
  FormGroup,
  Divider,
  Tooltip,
  OutlinedInput,
  Stack,
  FormControlLabel,
  Checkbox,
  Radio,
  IconButton,
  Chip,
  InputLabel,
  TextField,
  InputAdornment,
  List
} from '@mui/material';
import { Calendar, CloseCircle, FilterAdd, SearchNormal1, TagCross } from 'iconsax-react';
import useConfig from 'hooks/useConfig';
import { FormattedMessage, useIntl } from 'react-intl';
import { dispatch } from 'store';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useAuth from 'hooks/useAuth';

// ==============================|| DIALOG - SIZES ||============================== //
interface FilterProps {
  type: { val1: Array<string>; val2: string };

  title: string;
  count?: number;
  setFilterKey: any;
  value: any;
  handleCloseOnSearch: any;
  disabled: any;
  setDisabled: any;
  pageIndex?: any;
  selectedEndDate?: any;
  selectedStartDate?: any;
  handleApplyFilters?: any;
}

const FilterComponent = ({
  type,
  title,
  count,
  setFilterKey,
  value,
  handleCloseOnSearch,
  disabled,
  setDisabled,
  pageIndex,
  selectedStartDate,
  selectedEndDate,
  handleApplyFilters
}: FilterProps) => {
  const { i18n } = useConfig();
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const [range, setRange] = useState(!!(selectedStartDate || selectedEndDate));
  function capitalizeFirstLetter(str: any) {
    if (typeof str !== 'string') return str;
    return str
      .split(' ')
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  const [startDate, setStartDate] = useState(selectedStartDate);
  const [endDate, setEndDate] = useState(selectedEndDate);

  // Handler for date change
  const handleDateChange = (date: any, type: any) => {
    if (type === 'start') {
      setStartDate(date);
    } else if (type === 'end') {
      setEndDate(date);
    }
  };

  useEffect(() => {
    endDate && setFilterKey('endDate', endDate);
    startDate && setFilterKey('startDate', startDate);
  }, [endDate, startDate]);

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h5">
        <FormattedMessage id={capitalizeFirstLetter(title)} />
      </Typography>
      {typeof type === 'string' && (
        <Stack direction="row" spacing={1} sx={{ py: 1, mr: 2, justifyContent: 'left', alignItems: 'center' }} alignItems="center">
          <Stack>
            <OutlinedInput
              value={search || value || ''}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                setFilterKey('search', val);
              }}
              placeholder={getPlaceholder(i18n, count)}
              id="start-adornment-email"
              endAdornment={
                search || value ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => {
                        setSearch('');
                        setFilterKey('search', '');
                      }}
                    >
                      <CloseCircle size="18" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Stack>
          <Stack>
            <Tooltip title={<FormattedMessage id="Search" />}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  if (search) {
                    if (pageIndex) dispatch(pageIndex(1));

                    handleApplyFilters();
                  }
                }}
                sx={{ p: '9px', minWidth: 'auto' }}
              >
                <SearchNormal1 />
              </Button>
            </Tooltip>
          </Stack>
          {range ? (
            <></>
          ) : (
            <Stack>
              <Tooltip title={<FormattedMessage id="Select Range" />}>
                <Button variant="outlined" color="primary" onClick={() => setRange(true)} sx={{ p: '9px', minWidth: 'auto' }}>
                  <Calendar />
                </Button>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      )}
      {typeof type === 'string' && (
        <>
          {range ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h5">
                <FormattedMessage id="Range" />
              </Typography>
              <Stack direction={'row'} spacing={1} mt={1} mb={1}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Stack>
                    <InputLabel htmlFor="cal-startDate-date">
                      <FormattedMessage id="Start Date" />
                    </InputLabel>
                    <MobileDatePicker
                      value={startDate}
                      format="dd/MM/yyyy"
                      onChange={(date) => handleDateChange(date, 'start')}
                      disabled={disabled}
                      closeOnSelect={true}
                      slotProps={{
                        textField: {
                          id: 'cal-startDate-date',
                          placeholder: intl.formatMessage({ id: 'Start Date' }),
                          fullWidth: true,
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Calendar />
                              </InputAdornment>
                            )
                          }
                        }
                      }}
                    />
                  </Stack>
                  <Stack>
                    <InputLabel htmlFor="cal-endDate-date">
                      <FormattedMessage id="End Date" />
                    </InputLabel>
                    <MobileDatePicker
                      value={endDate}
                      format="dd/MM/yyyy"
                      onChange={(date) => handleDateChange(date, 'end')}
                      closeOnSelect={true}
                      minDate={startDate}
                      disabled={!startDate}
                      slotProps={{
                        textField: {
                          id: 'cal-endDate-date',
                          placeholder: intl.formatMessage({ id: 'End Date' }),
                          fullWidth: true,
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Calendar style={{ color: !startDate ? 'grey' : 'inherit' }} />
                              </InputAdornment>
                            )
                          }
                        }
                      }}
                    />
                  </Stack>

                  <Stack>
                    <IconButton
                      sx={{ mt: 2.5 }}
                      onClick={() => {
                        setRange(false);
                        setStartDate(null);
                        setEndDate(null);
                        setFilterKey('startDate', null);
                        setFilterKey('endDate', null);
                      }}
                      size="large"
                      color="error"
                    >
                      <TagCross size={44} />
                    </IconButton>
                  </Stack>
                </LocalizationProvider>
              </Stack>
              <Typography variant="caption" color={'red'} sx={{ mt: 2 }}>
                {' '}
                <FormattedMessage id="*note: End date can only be selected after the Start Date" />
              </Typography>
            </>
          ) : (
            <></>
          )}
        </>
      )}
      <List disablePadding component="nav" aria-label="main mailbox folders" sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {type &&
          Array.isArray(type?.val1) &&
          type?.val1 &&
          type?.val1?.length > 0 &&
          type?.val1.map((item, index: any) => {
            let isChecked;
            if (type?.val2 === 'checkbox') {
              if (value?.names) {
                isChecked = value.names.includes(item);
              } else {
                isChecked = value.includes(item);
              }
            } else {
              isChecked = value === item;
            }
            return (
              <Box key={item}>
                <FormControl component="fieldset">
                  <FormGroup aria-label="position" row>
                    <FormControlLabel
                      key={item}
                      id={index}
                      name={title}
                      value={item}
                      control={type?.val2 === 'checkbox' ? <Checkbox disabled={disabled} /> : <Radio disabled={disabled} />}
                      label={item}
                      labelPlacement="end"
                      sx={{ ml: 1 }}
                      checked={isChecked}
                      onChange={(e: any) => {
                        setFilterKey(title, index);
                        if (pageIndex) dispatch(pageIndex(1));
                        setDisabled(true);
                      }}
                    />
                  </FormGroup>
                </FormControl>
              </Box>
            );
          })}
      </List>
    </>
  );
};

type ModalFilterProps = Readonly<{
  filterKeywords?: any;
  onFilterKeywordChange: any;
  values: any;
  count?: number;
  disabled?: any;
  setDisabled?: any;
  pageIndex?: any;
  applyFilter?: any;
  filterFlag?: any;
  filterValuesSet?: any;
  setFilterSubmitValues?: any;
  submittedFilterValues?: any;
  setTabNotChange?: any;
}>;

export default function ModalFilter({
  filterKeywords,
  onFilterKeywordChange,
  values,
  count,
  disabled,
  setDisabled,
  pageIndex,
  applyFilter,
  filterFlag,
  filterValuesSet,
  setFilterSubmitValues,
  submittedFilterValues,
  setTabNotChange
}: ModalFilterProps) {

  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const { user } = useAuth();

  const handleClose = () => {
    setOpen(false);
    filterValuesSet(submittedFilterValues);
  };
  const handleCloseOnSearch = () => {
    applyFilter(!filterFlag);
    setOpen(false);
    setTabNotChange?.();
  };
  const handleApplyFilters = () => {
    applyFilter(!filterFlag);
    setTabNotChange?.();
    setOpen(false);
    setFilterSubmitValues(values);
  };
  return (
    <>
      <Box sx={{ mr: 2, ml: 2, justifyContent: 'center', display: 'flex' }}>
        {/* <Tooltip title={<FormattedMessage id="Filters" />}> */}
        <Chip
          color="primary"
          variant={user?.type === 'student' ? 'filled' : 'light'}
          icon={<FilterAdd variant="Bold" />}
          onClick={handleClickOpen}
          label={<FormattedMessage id="Filter" />}
        />
      </Box>

      <Dialog fullWidth={true} maxWidth={'md'} open={open} onClose={handleClose}>
        <IconButton
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none'
          }}
          onClick={handleClose}
        >
          <CloseCircle />
        </IconButton>
        <Box sx={{ p: 1, py: 1.5 }}>
          <DialogTitle>
            <FormattedMessage id="Filters" />
          </DialogTitle>

          <DialogContent>
            {Object.entries(filterKeywords).map(([key, value]: any) => (
              <FilterComponent
                key={key} // Make sure to add a unique key for each component in the array
                type={value}
                title={key}
                value={values[key]}
                count={count}
                setFilterKey={onFilterKeywordChange}
                handleCloseOnSearch={handleCloseOnSearch}
                disabled={disabled}
                setDisabled={setDisabled}
                pageIndex={pageIndex}
                selectedStartDate={values?.startDate}
                selectedEndDate={values?.endDate}
                handleApplyFilters={handleApplyFilters}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={handleApplyFilters}>
              <FormattedMessage id="Apply Filter" />
            </Button>
            <Button variant="outlined" color="error" onClick={handleClose}>
              <FormattedMessage id="Close" />
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
